import { BASIC_INFO_FIELDS, CONSTRUCTION_NATURE_OPTIONS, PROJECT_CATEGORY_OPTIONS } from "../constants";
import type { ProjectSummary } from "../types";
import { renderProjectOverviewSections } from "./project-overview-page";
import { escapeHtml, renderPagePath } from "../view-helpers";

type BasicInfoSectionSummary = {
  label: string;
  stateLabel: string;
  warning?: boolean;
  detail: string;
};

function hasFilledValue(values: string[]): boolean {
  return values.some((value) => value.trim().length > 0);
}

function buildBasicInfoSummaries(project: ProjectSummary): BasicInfoSectionSummary[] {
  const basicInfoReady = Boolean(
    project.basicInfo.projectCategory.trim()
      && project.basicInfo.projectName.trim()
      && project.basicInfo.projectAddress.trim()
      && project.basicInfo.constructionNature.length,
  );
  const companyReady =
    hasFilledValue(project.basicInfo.constructionUnits)
    && hasFilledValue(project.basicInfo.designCompanies)
    && hasFilledValue(project.basicInfo.constructionCompanies);
  const buildingUnitCount = project.overview.buildingUnits.filter((unit) =>
    [
      unit.buildingName,
      unit.structureType,
      unit.usageType,
      unit.fireResistanceLevel,
      unit.aboveGroundFloorCount,
      unit.buildingHeight,
    ].some((value) => value.trim().length > 0),
  ).length;
  const overviewReady = buildingUnitCount > 0;
  const facilityReady = project.overview.fireFacilitySystems.length > 0;
  const declarationStatus =
    project.declaration.entryMode === "pdf"
      ? "已留存 PDF"
      : project.declaration.entryMode === "photo"
        ? `已留存 ${project.declaration.photoDataUrls.length} 张照片`
        : project.declaration.entryMode === "skip"
          ? "稍后补传"
          : "未留存";

  return [
    {
      label: "项目信息",
      stateLabel: basicInfoReady ? "已填写" : "待补充",
      warning: !basicInfoReady,
      detail: basicInfoReady ? "工程类别、项目名称、地址和建设性质已具备基础内容。" : "工程类别、项目名称、地址或建设性质仍有缺项。",
    },
    {
      label: "参建单位",
      stateLabel: companyReady ? "已填写" : "待补充",
      warning: !companyReady,
      detail: companyReady ? "建设单位、设计企业、施工企业都已有内容。" : "建设单位、设计企业、施工企业至少还有一项未补齐。",
    },
    {
      label: "项目概况",
      stateLabel: overviewReady ? `已录入 ${buildingUnitCount} 个单体` : "待补充",
      warning: !overviewReady,
      detail: overviewReady ? "建筑单体信息已开始录入，可继续完善装修装饰和建筑保温。" : "建筑单体、装修装饰和建筑保温还没开始补充。",
    },
    {
      label: "消防设施",
      stateLabel: facilityReady ? `已勾选 ${project.overview.fireFacilitySystems.length} 项` : "待勾选",
      warning: !facilityReady,
      detail: facilityReady ? "消防设施勾选结果会直接用于报告“本工程设有……”句子。" : "消防设施还没勾选，报告相关句子暂时无法完整生成。",
    },
    {
      label: "申报表辅助",
      stateLabel: declarationStatus,
      warning: project.declaration.entryMode === "none",
      detail: "这里只是辅助入口；当前页仍以手动填写为主。",
    },
  ];
}

function renderCompanyGroup(
  title: string,
  inputName: string,
  values: string[],
  addAction: string,
): string {
  return `
    <div class="field">
      <div class="inline-head">
        <span>${title}</span>
        <button class="ghost-button small-button" type="button" data-action="${addAction}">+ 增加一行</button>
      </div>
      <div class="dynamic-list" data-list-name="${inputName}">
        ${values
          .map(
            (value, index) => `
              <div class="dynamic-list-row">
                <input name="${inputName}" type="text" value="${escapeHtml(value)}" placeholder="填写${title}" />
                ${
                  values.length > 1
                    ? `<button class="ghost-button small-button" type="button" data-remove-list-row="${inputName}" data-remove-index="${index}">删除</button>`
                    : ""
                }
              </div>
            `,
          )
          .join("")}
      </div>
    </div>
  `;
}

function renderDeclarationAssistCard(project: ProjectSummary): string {
  const currentMode = project.declaration.entryMode;
  const recognition = project.declaration.recognition;
  const modeLabel =
    currentMode === "pdf"
      ? "当前已保存：PDF 上传"
      : currentMode === "photo"
        ? `当前已保存：拍照上传 ${project.declaration.photoDataUrls.length} 张`
        : currentMode === "skip"
          ? "当前状态：稍后补传"
          : "当前还未留存验收申请表附件";

  const summary = project.declaration.sourceSummary
    || (currentMode === "skip"
      ? "这个项目目前按纯手动录入推进，后面仍可回来补传验收申请表。"
      : "如果手头有验收申请表，可以补传到辅助入口页留存附件，后续再按需识别或手动核对。");
  const recognitionSummary =
    recognition.status === "recognized"
      ? `辅助预填结果已生成${recognition.recognitionRate ? `，当前识别率 ${recognition.recognitionRate}%` : ""}，你可以继续在本页按手动方式核对和修改。`
      : recognition.status === "running"
        ? "辅助预填当前正在处理中，当前页仍可继续手动填写，不需要停下来等待。"
        : recognition.status === "failed"
          ? "上次辅助预填没有成功，当前项目仍按手动填写主线推进。"
          : "当前页始终以手动填写为准，申报表附件和辅助预填都只作为补充能力。";
  const manageButtonLabel =
    currentMode === "pdf" || currentMode === "photo" ? "查看/补传验收申请表" : "去补传验收申请表";

  return `
    <div class="panel stack nested-panel">
      <div class="inline-head">
        <div>
          <p class="eyebrow">验收申请表辅助入口</p>
          <p class="muted">当前页仍以手动填写为主，这里只负责补传附件和辅助预填。</p>
        </div>
        <button class="ghost-button small-button" type="button" data-action="go-declaration-manage">${manageButtonLabel}</button>
      </div>
      <div class="inline-pills">
        <span class="state-pill">${escapeHtml(modeLabel)}</span>
      </div>
      <p class="mini-meta">${escapeHtml(summary)}</p>
      <p class="mini-meta">${escapeHtml(recognitionSummary)}</p>
    </div>
  `;
}

export function renderProjectBasicInfoPage(project: ProjectSummary): string {
  const fields = BASIC_INFO_FIELDS.map((field) => {
    const value = project.basicInfo[field.key] || "";

    if (field.multiline) {
      return `
        <label class="field">
          <span>${field.label}</span>
          <textarea name="${field.key}" placeholder="${field.placeholder}" rows="3">${escapeHtml(String(value))}</textarea>
        </label>
      `;
    }

    return `
      <label class="field">
        <span>${field.label}</span>
        <input name="${field.key}" type="text" value="${escapeHtml(String(value))}" placeholder="${field.placeholder}" />
      </label>
    `;
  }).join("");

  return `
    <section class="screen">
      <header class="topbar">
        <button class="ghost-button" data-action="back-to-home">返回项目</button>
        <h1>项目基本信息</h1>
      </header>
      ${renderPagePath([
        { label: project.name, href: `#/projects/home?id=${project.id}` },
        { label: "项目基本信息" },
      ])}
      <form id="basic-info-form" class="panel stack">
        <p class="muted stage-note">这里已经切到纯手动录入模式，项目基本信息和项目概况都以你当前填写的内容为准。</p>
        <p class="mini-meta">输入时会自动保存到本地，弱网或断网时也不会因为没点保存就丢掉当前内容。</p>
        <div class="field">
          <span>工程类别</span>
          <div class="checkbox-grid">
            ${PROJECT_CATEGORY_OPTIONS.map(
              (option) => `
                <label class="check-chip">
                  <input type="radio" name="projectCategory" value="${option}" ${
                    project.basicInfo.projectCategory === option ? "checked" : ""
                  } />
                  <span>${option}</span>
                </label>
              `,
            ).join("")}
          </div>
        </div>
        ${fields}
        <div class="field">
          <span>建设性质</span>
          <div class="checkbox-grid">
            ${CONSTRUCTION_NATURE_OPTIONS.map(
              (option) => `
                <label class="check-chip">
                  <input type="checkbox" name="constructionNature" value="${option}" ${
                    project.basicInfo.constructionNature.includes(option) ? "checked" : ""
                  } />
                  <span>${option}</span>
                </label>
              `,
            ).join("")}
          </div>
        </div>
        ${renderCompanyGroup("建设单位", "constructionUnits", project.basicInfo.constructionUnits, "add-construction-unit")}
        ${renderCompanyGroup("设计企业", "designCompanies", project.basicInfo.designCompanies, "add-design-company")}
        ${renderCompanyGroup("施工企业", "constructionCompanies", project.basicInfo.constructionCompanies, "add-construction-company")}
        ${renderCompanyGroup("监理企业", "supervisionCompanies", project.basicInfo.supervisionCompanies, "add-supervision-company")}
        <div class="panel stack nested-panel">
          <div class="inline-head">
            <div>
              <p class="eyebrow">项目概况</p>
              <p class="muted">单体信息、装修装饰和建筑保温统一放在这一页里维护。</p>
            </div>
            <button class="ghost-button small-button" type="button" data-action="add-building-unit">+ 增加建筑单体</button>
          </div>
          <p class="mini-meta">“本工程设有 xxx 等消防设施”这一句在本页底部通过勾选消防设施生成，不需要另外手写。</p>
          ${renderProjectOverviewSections(project)}
        </div>
        <div id="basic-info-save-status" class="save-feedback" aria-live="polite"></div>
        <button class="nav-button" type="button" data-action="go-receiving">进入项目接件信息</button>
        <button class="primary-button" type="submit">保存基础信息</button>
      </form>
    </section>
  `;
}
